import { SUPPORT_KIND_LABEL } from '@/features/support-requests/types';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlarmClock,
  CalendarClock,
  IndianRupee,
  LifeBuoy,
  Loader2,
  MessageSquare,
  Store,
  User,
} from 'lucide-react';
import { DashboardLayout } from '@/components/dashboard-layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DetailPanel, Field, FieldList } from '@/components/detail/detail-panel';
import { MetricStrip, type Metric } from '@/components/metric-strip';
import { PersonAvatar } from '@/components/person-avatar';
import { AuthImage } from '@/components/auth-image';
import { Badge } from '@/components/ui/badge';
import { date_time, humanize, inr, title_case } from '@/lib/format';
import {
  OPENED_BY_LABELS,
  SUPPORT_STATUS_LABELS as STATUS_LABEL,
  type WalkerIdentity,
} from '@/features/support-requests/types';

/** One colour per status, matching the queue. */
const STATUS_TONE: Record<string, 'success' | 'warning' | 'neutral' | 'destructive' | 'info'> = {
  OPEN: 'warning',
  PROPOSED: 'info',
  RESOLVED: 'success',
  REFUNDED: 'destructive',
  CANCELLED: 'neutral',
};
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  use_nearby_walkers,
  use_support_actions,
  use_support_request_detail,
} from '@/features/support-requests/hooks';
import type { SupportRequestDetail } from '@/features/support-requests/types';

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (value == null || value === '') return null;
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-border py-2 text-sm last:border-b-0">
      <span className="font-semibold text-muted-foreground">{label}</span>
      <span className="col-span-2">{value}</span>
    </div>
  );
}

function fmt(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}

const err_msg = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

export function SupportRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: r, isLoading, error } = use_support_request_detail(id);

  const waiting = r?.waiting_days ?? 0;

  const metrics: Metric[] = [
    {
      key: 'waiting',
      label: r?.is_open ? 'Waiting' : 'Took',
      value: r ? (waiting === 1 ? '1 day' : `${waiting} days`) : undefined,
      hint: r?.is_open ? 'Since it was raised' : 'From raised to resolved',
      // Past a week on an open request, the platform is failing a promise.
      tone:
        r?.is_open && waiting >= 7
          ? 'destructive'
          : r?.is_open && waiting >= 3
            ? 'warning'
            : 'default',
      icon: AlarmClock,
      loading: isLoading,
    },
    {
      key: 'price',
      label: 'Session price',
      value: r ? inr(r.booking.price_inr) : undefined,
      hint: r?.refund_amount_inr ? `${inr(r.refund_amount_inr)} refunded` : undefined,
      tone: r?.refund_amount_inr ? 'destructive' : 'default',
      icon: IndianRupee,
      loading: isLoading,
    },
    {
      key: 'booking_status',
      label: 'The booking',
      value: r ? humanize(r.booking.status) : undefined,
      hint: r ? date_time(r.booking.slot_start) : undefined,
      icon: CalendarClock,
      loading: isLoading,
    },
    {
      key: 'raised_by',
      label: 'Raised by',
      value: r ? (OPENED_BY_LABELS[r.opened_by] ?? title_case(r.opened_by)) : undefined,
      hint: r?.opened_by === 'SYSTEM' ? 'The no-show sweep, not a person' : undefined,
      icon: User,
      loading: isLoading,
    },
  ];

  return (
    <DashboardLayout
      title={r ? (SUPPORT_KIND_LABEL[r.kind] ?? r.kind) : 'Support request'}
      subtitle={
        r
          ? `${r.booking.service_name ?? 'Service removed'} · raised ${date_time(r.created_at)}`
          : undefined
      }
      breadcrumbs={[{ label: r ? (SUPPORT_KIND_LABEL[r.kind] ?? r.kind) : 'Support request' }]}
      avatar={
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-panel bg-muted text-muted-foreground">
          <LifeBuoy aria-hidden="true" className="h-5 w-5" />
        </span>
      }
      title_badges={
        r ? (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge
              tone={STATUS_TONE[r.status] ?? 'neutral'}
              size="sm"
              dot
              data-testid={`support-status-${r.status}`}
            >
              {STATUS_LABEL[r.status] ?? humanize(r.status)}
            </Badge>
            {r.is_open && waiting >= 7 && (
              <Badge tone="destructive" size="sm" dot data-testid="badge-stale">
                waiting {waiting} days
              </Badge>
            )}
            {!r.reason && (
              <Badge tone="warning" size="sm" dot>
                No reason given
              </Badge>
            )}
            {r.booking.walk_plan_id && (
              <Badge tone="info" size="sm">
                Part of a walk plan
              </Badge>
            )}
            {r.booking.training_package_id && (
              <Badge tone="info" size="sm">
                Part of a course
              </Badge>
            )}
          </span>
        ) : undefined
      }
      actions={
        r ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/support-requests" data-testid="back-to-support">
                Back to the queue
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/bookings/${r.booking.id}`} data-testid="open-booking">
                <CalendarClock className="h-4 w-4" /> Booking
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/users/${r.booking.user_id}`} data-testid="open-customer">
                <User className="h-4 w-4" /> Customer
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to={`/vendors/${r.booking.vendor_profile_id}`} data-testid="open-vendor">
                <Store className="h-4 w-4" /> Vendor
              </Link>
            </Button>
          </div>
        ) : undefined
      }
      stats={<MetricStrip metrics={metrics} />}
    >
      {error ? (
        <div
          className="rounded-md border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive"
          data-testid="support-detail-error"
        >
          This request isn't available.
        </div>
      ) : isLoading || !r ? (
        <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* What the person actually said, in their own words, first. */}
          <div
            className={`flex items-start gap-2 rounded-panel px-3 py-2.5 text-data ${r.reason ? 'bg-muted/60' : 'bg-warning/10 text-warning'}`}
            data-testid="support-reason-banner"
          >
            <MessageSquare aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {r.reason ? (
                <>
                  <strong className="font-semibold">
                    {OPENED_BY_LABELS[r.opened_by] ?? title_case(r.opened_by)}:
                  </strong>{' '}
                  “{r.reason}”
                </>
              ) : (
                <>
                  <strong className="font-semibold">No reason was recorded.</strong> Nothing here
                  says what went wrong, so this one needs a call before it can be triaged.
                </>
              )}
            </span>
          </div>

          <div className="grid items-start gap-gutter lg:grid-cols-2">
            <DetailPanel title="The session" icon={CalendarClock} test_id="support-detail">
              <FieldList>
                <Field label="Service" value={r.booking.service_name} empty="Service removed" />
                <Field
                  label="Pet"
                  value={
                    r.booking.pet
                      ? `${r.booking.pet.name} · ${title_case(r.booking.pet.species)}`
                      : null
                  }
                  empty="No pet on this booking"
                />
                <Field
                  label="Slot"
                  value={`${fmt(r.booking.slot_start)} – ${new Date(r.booking.slot_end).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}`}
                />
                <Field label="Booking status" value={humanize(r.booking.status)} />
                <Field label="Price" value={inr(r.booking.price_inr)} mono />
                <Field
                  label="Booking"
                  value={
                    <Link to={`/bookings/${r.booking.id}`} className="text-primary hover:underline">
                      Open the booking
                    </Link>
                  }
                />
              </FieldList>
            </DetailPanel>

            <DetailPanel title="Who is involved" icon={User} test_id="support-parties">
              <FieldList>
                <Field
                  label="Customer"
                  value={
                    <Link
                      to={`/users/${r.booking.user_id}`}
                      className="text-primary hover:underline"
                    >
                      {r.booking.customer_name ?? r.booking.customer_phone ?? 'No name on file'}
                    </Link>
                  }
                />
                <Field label="Customer phone" value={r.booking.customer_phone} mono empty="None" />
                <Field
                  label="Contact on the day"
                  value={r.booking.contact_phone}
                  mono
                  empty="Uses the account number"
                />
                <Field
                  label="Vendor"
                  value={
                    <Link
                      to={`/vendors/${r.booking.vendor_profile_id}`}
                      className="text-primary hover:underline"
                    >
                      {r.booking.vendor_business_name ?? 'Vendor removed'}
                    </Link>
                  }
                />
                <Field label="Pickup" value={r.booking.pickup_address} empty="Not given" />
                {r.resolved_at && <Field label="Resolved" value={fmt(r.resolved_at)} />}
                {r.payout_amount_inr && (
                  <Field label="Reallocated payout" value={inr(r.payout_amount_inr)} mono />
                )}
                {r.refund_amount_inr && (
                  <Field label="Refunded" value={inr(r.refund_amount_inr)} mono />
                )}
              </FieldList>
            </DetailPanel>
          </div>

          {r.payout_rollup && r.payout_rollup.length > 0 && (
            <Card data-testid="support-payout-rollup">
              <CardHeader>
                <CardTitle>Plan payout allocation</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col">
                {r.payout_rollup.map((p) => (
                  <Row
                    key={p.vendor_profile_id}
                    label={p.vendor_business_name ?? p.vendor_profile_id}
                    value={`${p.walks} walk(s) · ₹${p.amount_inr}`}
                  />
                ))}
              </CardContent>
            </Card>
          )}

          {/*
            A no-show is refund-only. The slot has already passed, so there is no walk left to
            hand to another walker — offering reassignment there would be promising a service at
            a time that is gone.
          */}
          {r.kind === 'WALKER_REASSIGN' && r.status === 'OPEN' && <ReassignPanel r={r} />}
          {r.kind === 'TRAINER_RESCHEDULE' && (r.status === 'OPEN' || r.status === 'PROPOSED') && (
            <ReschedulePanel r={r} />
          )}
          {r.kind === 'VENDOR_NO_SHOW' && r.status === 'OPEN' && (
            <Card data-testid="support-no-show-panel">
              <CardHeader>
                <CardTitle>Refund this session</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  The slot passed without the vendor ever accepting, so the customer paid for
                  something that never happened. A full refund is the default here — the amount is
                  pre-filled with the session price.
                </p>
                <RefundBlock r={r} noun="session" emphasise prompt="Refund the customer in full." />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

// ---- Walker reassignment ----
/** How many candidates to show before the action, rather than after all of them. */
const SHORTLIST = 6;

/**
 * A walker as a person: who they are, how long they have done this, what they
 * are good at, and whether they can talk to the owner.
 *
 * A walker vendor is two records — the VendorProfile that gets paid and the
 * PetWalkerProfile who actually turns up at somebody's door. This picker only
 * ever showed the first, so an admin handing over a stranger's dog was choosing
 * between rows like "E2E Sandbox Vendor - 0.0 km", five of which were the same
 * person. Every fact here is one you would ask about on the phone.
 */
function WalkerCard({
  walker,
  business_name,
  business_photo_path,
  city,
  meta,
  compact,
}: {
  walker: WalkerIdentity | null;
  business_name: string | null;
  /** The listing photo, shown small beside the business name. */
  business_photo_path?: string | null;
  city?: string | null;
  meta?: React.ReactNode;
  compact?: boolean;
}) {
  const facts = walker
    ? [
        walker.age !== null ? String(walker.age) : null,
        walker.gender && walker.gender !== 'OTHER' ? title_case(walker.gender) : null,
        `${walker.years_of_experience}y walking`,
      ].filter(Boolean)
    : [];

  return (
    <span className="flex min-w-0 flex-1 items-start gap-2.5">
      {walker?.photo_path ? (
        <AuthImage
          path={walker.photo_path}
          alt=""
          className="h-10 w-10 shrink-0 rounded-full object-cover"
        />
      ) : (
        <PersonAvatar
          path={null}
          name={walker?.full_name ?? business_name}
          seed={business_name ?? 'walker'}
          size="sm"
        />
      )}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-semibold">
            {walker?.full_name ?? business_name ?? 'Unnamed walker'}
          </span>
          {facts.length > 0 && (
            <span className="text-micro text-muted-foreground">{facts.join(' \u00b7 ')}</span>
          )}
          {/* Rare, and it means nothing proves the walk started or ended. */}
          {walker && !walker.require_walk_otp && (
            <Badge tone="warning" size="sm" className="whitespace-nowrap">
              no OTP check
            </Badge>
          )}
        </span>

        {walker && (walker.specializations.length > 0 || walker.languages.length > 0) && (
          <span className="flex flex-wrap items-center gap-1">
            {walker.specializations.map((sp) => (
              <Badge key={sp} tone="neutral" size="sm">
                {sp}
              </Badge>
            ))}
            {walker.languages.length > 0 && (
              // Whether they can actually talk to the owner.
              <span className="text-micro text-muted-foreground">
                speaks {walker.languages.join(', ')}
              </span>
            )}
          </span>
        )}

        {!compact && (
          <span className="flex min-w-0 items-center gap-1.5 text-micro text-muted-foreground">
            {/*
              The business's own photo, small enough to read as "this is the
              shop, not the person" — the face above is the one that matters.
            */}
            {business_photo_path && (
              <AuthImage
                path={business_photo_path}
                alt=""
                className="h-6 w-9 shrink-0 rounded-control object-cover"
              />
            )}
            <span className="truncate">
              {business_name ?? 'No business name'}
              {city ? ` \u00b7 ${city}` : ''}
            </span>
          </span>
        )}
        {meta}
      </span>
    </span>
  );
}

function ReassignPanel({ r }: { r: SupportRequestDetail }) {
  const nearby = use_nearby_walkers(r.id, true);
  const { reassign } = use_support_actions(r.id);
  const [vendor_id, set_vendor_id] = useState('');
  const [payout, set_payout] = useState(r.booking.payout_amount_inr ?? r.booking.price_inr);
  /*
   * Eighteen candidates come back on this data, so the whole list pushed the
   * payout field and the Reassign button below the fold — the operator had to
   * scroll past every option to reach the action. Show a shortlist, keep the
   * rest one click away.
   */
  const [show_all, set_show_all] = useState(false);
  const all = nearby.data?.items ?? [];
  const visible = show_all ? all : all.slice(0, SHORTLIST);
  const selected = all.find((w) => w.vendor_profile_id === vendor_id) ?? null;

  return (
    <Card data-testid="reassign-panel">
      <CardHeader>
        <CardTitle>Reassign to a nearby walker</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/*
          Who is being replaced. Without this an admin was choosing a replacement
          with no idea what the customer already had, so "6 years, speaks
          Kannada" meant nothing - better or worse than what?
        */}
        <div
          className="flex flex-col gap-1 rounded-panel bg-muted/50 p-2.5"
          data-testid="current-walker"
        >
          <span className="text-micro font-bold uppercase tracking-wide text-muted-foreground">
            Replacing
          </span>
          <div className="flex items-start justify-between gap-3 text-data">
            <WalkerCard
              walker={r.booking.current_walker}
              business_name={r.booking.vendor_business_name}
              business_photo_path={r.booking.current_business_photo_path}
            />
            <span className="shrink-0 text-micro text-muted-foreground">
              {r.booking.current_vendor_reviews
                ? `${Number(r.booking.current_vendor_rating).toFixed(1)}\u2605 (${r.booking.current_vendor_reviews})`
                : 'no reviews'}
              {r.booking.current_vendor_completed_walks !== null
                ? ` \u00b7 ${r.booking.current_vendor_completed_walks} done`
                : ''}
            </span>
          </div>
        </div>

        {nearby.isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Finding nearby walkers…
          </div>
        ) : !nearby.data || nearby.data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-nearby-walkers">
            {nearby.data && !nearby.data.center
              ? 'No location on the booking or the current vendor, so nearby walkers cannot be ranked.'
              : `No verified walkers within ${nearby.data?.search_radius_km ?? 25} km of this booking.`}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {visible.map((w) => {
              const active = vendor_id === w.vendor_profile_id;
              return (
                <li key={w.vendor_profile_id}>
                  <button
                    type="button"
                    onClick={() => set_vendor_id(w.vendor_profile_id)}
                    data-testid={`nearby-walker-${w.vendor_profile_id}`}
                    className={`flex w-full items-start justify-between gap-3 rounded-md border-2 px-3 py-2 text-left text-sm transition-colors ${active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                  >
                    <WalkerCard
                      walker={w.walker}
                      business_name={w.business_name ?? w.vendor_profile_id}
                      business_photo_path={w.business_photo_path}
                      city={w.city}
                      meta={
                        // Still selectable - just be honest that it is a stretch.
                        !w.covers_distance ? (
                          <span
                            className="text-micro font-semibold text-warning"
                            data-testid={`outside-radius-${w.vendor_profile_id}`}
                          >
                            outside their {w.service_radius_km} km radius
                          </span>
                        ) : undefined
                      }
                    />
                    {/*
                      A track record and a distance. The record matters more:
                      most candidates sit at the same coordinates, so distance
                      alone gave an operator nothing to pick on.
                    */}
                    <span className="flex shrink-0 flex-col items-end gap-0.5 text-micro tabular-nums text-muted-foreground">
                      <span className="whitespace-nowrap">
                        {/*
                          Under 100 m reads as "0.0 km", which looks like a missing value
                          rather than a measurement — and in this marketplace it is
                          common, because a walker registered at the same address as the
                          pickup really is zero away. Say what it means instead.
                        */}
                        {w.distance_km == null
                          ? '—'
                          : w.distance_km < 0.1
                            ? 'at the pickup'
                            : `${w.distance_km.toFixed(1)} km`}
                      </span>
                      <span className="whitespace-nowrap">
                        {w.review_count > 0
                          ? `${Number(w.avg_rating).toFixed(1)}★ (${w.review_count})`
                          : 'no reviews'}
                      </span>
                      <span className="whitespace-nowrap">{w.completed_walks} done</span>
                    </span>
                  </button>
                </li>
              );
            })}
            {all.length > SHORTLIST && (
              <li>
                <button
                  type="button"
                  onClick={() => set_show_all((v) => !v)}
                  className="text-data font-semibold text-primary hover:underline"
                  data-testid="toggle-all-walkers"
                >
                  {show_all
                    ? 'Show fewer'
                    : `Show all ${all.length} walkers within ${nearby.data.search_radius_km} km`}
                </button>
              </li>
            )}
          </ul>
        )}

        {/* Say who is about to be handed the walk, right next to the button. */}
        {selected && (
          <p
            className="rounded-panel bg-info/10 px-3 py-2 text-data text-info"
            data-testid="reassign-selection"
          >
            {/* The person, not the business — that is who turns up. */}
            Handing this walk to{' '}
            <strong className="font-semibold">
              {selected.walker?.full_name ?? selected.business_name ?? selected.vendor_profile_id}
            </strong>
            {selected.walker && selected.business_name ? ` at ${selected.business_name}` : ''}
            {selected.covers_distance
              ? ''
              : ` — further than the ${selected.service_radius_km} km they advertise`}
            . They still have to accept it.
          </p>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="payout">
              Payout to new walker (₹)
            </label>
            <Input
              id="payout"
              type="number"
              min={0}
              value={payout}
              onChange={(e) => set_payout(e.target.value)}
              className="w-40"
              data-testid="reassign-payout"
            />
          </div>
          <Button
            disabled={!vendor_id || reassign.isPending}
            onClick={() =>
              reassign.mutate({ vendor_profile_id: vendor_id, payout_amount_inr: Number(payout) })
            }
            data-testid="reassign-submit"
          >
            {reassign.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Reassign walk
          </Button>
        </div>
        {reassign.isError && (
          <p className="text-sm text-destructive" data-testid="reassign-error">
            {err_msg(reassign.error, 'Reassignment failed.')}
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          The new walker must accept the walk. Their payout is your reallocation of the prepaid
          amount.
        </p>

        {/* With no candidate to hand the walk to, refunding is the only way to close
            this out — so lead with it rather than leaving the request stranded. */}
        <RefundBlock
          r={r}
          noun="walk"
          emphasise={!nearby.isLoading && (!nearby.data || nearby.data.items.length === 0)}
          prompt={
            !nearby.isLoading && (!nearby.data || nearby.data.items.length === 0)
              ? 'No walker can cover this walk — refund it to close the request.'
              : 'Can’t place this walk? Refund it instead.'
          }
        />
      </CardContent>
    </Card>
  );
}

// ---- Trainer reschedule / refund ----
function ReschedulePanel({ r }: { r: SupportRequestDetail }) {
  const { propose } = use_support_actions(r.id);
  const [slot, set_slot] = useState('');

  return (
    <Card data-testid="reschedule-panel">
      <CardHeader>
        <CardTitle>Mediate reschedule or refund</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {r.status === 'PROPOSED' && (
          <div
            className="rounded-md border border-info/30 bg-info/10 p-3 text-sm"
            data-testid="proposed-state"
          >
            <p className="font-semibold">
              Proposed: {r.proposed_slot_start ? fmt(r.proposed_slot_start) : '—'}
            </p>
            <p className="text-muted-foreground">
              Trainer: {r.trainer_agreed ? 'agreed ✓' : 'pending'} · Customer:{' '}
              {r.user_agreed ? 'agreed ✓' : 'pending'}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-muted-foreground" htmlFor="slot">
              Propose a new time
            </label>
            <Input
              id="slot"
              type="datetime-local"
              value={slot}
              onChange={(e) => set_slot(e.target.value)}
              data-testid="propose-slot-input"
            />
          </div>
          <Button
            variant="outline"
            disabled={!slot || propose.isPending}
            onClick={() => propose.mutate(new Date(slot).toISOString())}
            data-testid="propose-submit"
          >
            {propose.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{' '}
            {r.status === 'PROPOSED' ? 'Re-propose' : 'Propose'}
          </Button>
        </div>
        {propose.isError && (
          <p className="text-sm text-destructive" data-testid="propose-error">
            {err_msg(propose.error, 'Proposal failed.')}
          </p>
        )}

        <RefundBlock r={r} prompt="No agreement possible? Refund this session." noun="session" />
      </CardContent>
    </Card>
  );
}

/**
 * Refund + cancel a single session. Shared by both support kinds: a trainer
 * reschedule nobody can agree on, and a walk with no nearby walker to hand it to.
 * Without this, a reassignment request with an empty candidate list had no
 * resolution at all and would sit OPEN indefinitely.
 */
function RefundBlock({
  r,
  prompt,
  noun,
  emphasise = false,
}: {
  r: SupportRequestDetail;
  prompt: string;
  noun: string;
  emphasise?: boolean;
}) {
  const { refund } = use_support_actions(r.id);
  const [amount, set_amount] = useState(r.booking.price_inr);

  return (
    <div
      className={
        emphasise
          ? 'rounded-md border-2 border-warning/40 bg-warning/10 p-3'
          : 'border-t border-border pt-4'
      }
      data-testid="refund-block"
    >
      <p className="mb-2 text-sm font-semibold">{prompt}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-muted-foreground" htmlFor="refund-amount">
            Refund amount (₹)
          </label>
          <Input
            id="refund-amount"
            type="number"
            min={0}
            value={amount}
            onChange={(e) => set_amount(e.target.value)}
            className="w-40"
            data-testid="refund-amount"
          />
        </div>
        <Button
          variant="destructive"
          disabled={refund.isPending}
          onClick={() => refund.mutate(Number(amount))}
          data-testid="refund-submit"
        >
          {refund.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Refund &amp;
          cancel {noun}
        </Button>
      </div>
      {refund.isError && (
        <p className="mt-2 text-sm text-destructive" data-testid="refund-error">
          {err_msg(refund.error, 'Refund failed.')}
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Partial refund of the plan/package payment; the {noun} is cancelled.
      </p>
    </div>
  );
}
